import { z } from 'zod'

import type { TaskStatus } from './status'
import { dringlichkeitVon, type Dringlichkeit, type Termin } from './planung'
import { entstanden } from './zeit'
import type { WorkspaceArt } from './workspaces'

// `DEFAULT_WIP_LIMIT` liegt in roles.ts, wo die Person definiert wird – hier nur
// benutzt, nicht erneut festgelegt.

/**
 * Pools – thematische Vorräte, aus denen gezogen wird (Konzept Kap. 4.2).
 *
 * ## Die häufigste Fehldeutung, gleich am Anfang ausgeräumt
 *
 * **Ein Pool ist keine Prioritätsstufe.** Die Priorität A/B/C hängt an der
 * Aufgabe und sortiert *innerhalb* eines Pools. Wer Pools als Dringlichkeit
 * baut, bekommt „Sofort" als vierte Prio-Stufe und danach drei konkurrierende
 * Achsen, die niemand mehr auseinanderhält.
 *
 * `sofort` ist kein „Prio A+", sondern ein Vorrat mit anderen Spielregeln: er
 * darf das persönliche WIP-Limit durchbrechen.
 *
 * ## Eine Aufgabe kann in mehreren Pools liegen
 *
 * Deshalb ist die Zugehörigkeit eine eigene Tabelle und keine Spalte an der
 * Aufgabe. Ein Ticket kann „Tickets & Kleinigkeiten" **und** „Projektaufgaben"
 * betreffen; gezogen wird es einmal und verschwindet dann aus allen (Kap. 4.3).
 */

/** Startbelegung aus Konzept und Mockup. Weitere Pools sind Zeilen in der Tabelle. */
export const POOL_KEYS = ['sofort', 'tickets', 'projekte', 'parkplatz'] as const
export type PoolKey = (typeof POOL_KEYS)[number]
export const poolKeySchema = z.string().min(1).max(32)

/**
 * **Der Parkplatz** – der eine Pool, aus dem nicht gezogen wird (20.08.2026,
 * [E-191](../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Die Konstante steht da, weil Server und Oberfläche denselben Pool meinen müssen, wenn sie
 * „parken" sagen. Die *Regel* dagegen hängt nicht daran, sondern an `ziehbar` – ein zweiter
 * Parkplatz wäre eine Zeile in der Tabelle und kein zweiter Sonderfall im Code.
 *
 * ## Seit Z-07 ist das ein **Schlüssel**, keine Kennung
 *
 * Jeder Workspace hat seinen eigenen Parkplatz, und außerhalb von `entwicklung` heißt der
 * nicht `parkplatz`. Wer parken will, sucht deshalb den Pool **dieses Workspace** mit
 * `schluessel = PARKPLATZ_POOL` – nicht den Pool mit dieser Kennung. In `entwicklung` sind
 * beide Werte gleich, und genau das macht den Unterschied so leicht zu übersehen.
 */
export const PARKPLATZ_POOL = 'parkplatz'

/**
 * Die Pools, mit denen Work startet.
 *
 * Ob es noch einen braucht („Intern/Technik" für Refactorings, Doku, Wartung), ist
 * [F-07](../../../docs/OFFENE-FRAGEN.md) – und dann eine Zeile, kein Umbau.
 */
export const POOL_STARTBELEGUNG: {
  id: PoolKey
  /**
   * **Bedeutung, getrennt von der Kennung** ([Z-07](../../../docs/phasen/zusatz-07-workspaces.md)).
   *
   * Bei diesen vier ist beides gleich – sie sind die Ersten und behalten ihre Kennungen. Ab
   * dem zweiten Workspace fällt es auseinander: dessen „Sofort" heißt `mkt-sofort` und trägt
   * hier trotzdem `sofort`, damit Farbe und die Parken-Zielsuche ihn finden.
   *
   * Ein **selbst angelegter** Pool hat gar keinen Schlüssel (`null` in der Datenbank) – das
   * ist die ehrliche Aussage „keiner der vier bekannten".
   */
  schluessel: PoolKey
  label: string
  icon: string
  sortOrder: number
  limitAnzahl: number | null
  regeln: string
  wipDurchbrechen: boolean
  jederDarfZiehen: boolean
  ziehbar: boolean
  vorlaufNurMitTermin: boolean
}[] = [
  {
    id: 'sofort',
    schluessel: 'sofort',
    label: 'Sofort',
    icon: '🔥',
    sortOrder: 1,
    // Bewusst klein: ein „Sofort"-Vorrat mit zwanzig Aufgaben ist kein Sofort
    // mehr, sondern eine zweite Warteschlange.
    limitAnzahl: 5,
    regeln: 'Geht immer vor und darf das persönliche WIP-Limit durchbrechen.',
    wipDurchbrechen: true,
    jederDarfZiehen: true,
    ziehbar: true,
    vorlaufNurMitTermin: false,
  },
  {
    id: 'tickets',
    schluessel: 'tickets',
    label: 'Tickets & Kleinigkeiten',
    icon: '🎫',
    sortOrder: 2,
    limitAnzahl: null,
    regeln: 'Kundenmeldungen und Kleinteiliges. Sortiert nach Priorität, dann Alter.',
    wipDurchbrechen: false,
    jederDarfZiehen: true,
    ziehbar: true,
    vorlaufNurMitTermin: false,
  },
  {
    id: 'projekte',
    schluessel: 'projekte',
    label: 'Projektaufgaben',
    icon: '📦',
    sortOrder: 3,
    limitAnzahl: null,
    regeln: 'Arbeit an Vorhaben. Braucht zusammenhängende Zeit.',
    wipDurchbrechen: false,
    jederDarfZiehen: true,
    ziehbar: true,
    vorlaufNurMitTermin: false,
  },
  /**
   * **Der Parkplatz** – Nils, 20.08.2026: *„Ich brauche noch einen Pool zu einer Art
   * «Abwurf», wo ich die, die ich nur zur Info habe, erst mal hinwerfen kann. Die sollen
   * sich nicht gezogen werden können, das dient nur dazu, die erst mal aus dem Eingang
   * rauszubekommen. … daraus entsteht dann meist eine neue Aufgabe, aber mit Infos, die ich
   * da parke."*
   *
   * ## Warum das trotzdem ein Pool ist und keine eigene Ansicht
   *
   * Weil er dieselben drei Fragen beantwortet wie die anderen: *was liegt hier, wie viel,
   * seit wann*. Was ihn unterscheidet, ist eine Regel – und Regeln sind an diesem Pool schon
   * seit Phase 4 Spalten (`limitAnzahl`, `wipDurchbrechen`, `jederDarfZiehen`). `ziehbar` ist
   * die vierte.
   *
   * ## Warum er zuletzt steht
   *
   * `sortOrder` ist die Wichtigkeit, und der Trichter liest sie von unten nach oben. Ein
   * Regal ist der unwichtigste Vorrat, den es gibt – es *ist* keiner.
   */
  {
    id: 'parkplatz',
    schluessel: 'parkplatz',
    label: 'Parkplatz',
    icon: '🅿️',
    sortOrder: 90,
    /**
     * **Kein Limit, und das ist keine Nachlässigkeit.** Ein volles Regal ist keine Aussage
     * über die Lage – niemand arbeitet es ab. Eine Warnung dort wäre eine, die man wegklickt,
     * und die nächste echte gleich mit.
     */
    limitAnzahl: null,
    regeln:
      'Nur zur Kenntnis – hier wird nichts gezogen. Was hier liegt, ist geparkt, bis daraus ' +
      'eine Aufgabe wird; der Weg zurück führt in den Eingang.',
    wipDurchbrechen: false,
    jederDarfZiehen: true,
    ziehbar: false,
    vorlaufNurMitTermin: false,
  },
]

/**
 * **Die Vorräte** – die Pools, aus denen wirklich gezogen wird.
 *
 * Überall dort zu benutzen, wo ein Pool zur *Wahl* steht (Einordnen, Triage-Regel, Vorhaben,
 * KI-Vorschlag) oder wo Ebenen daraus entstehen (Trichter, „Aufgabe ziehen"). Ein Regal in
 * einem Auswahlfeld wäre ein Angebot, das der Server ablehnt – und in einer Trichter-Ebene
 * eine Stufe, die niemand abarbeitet.
 *
 * Die Pools-Ansicht selbst benutzt sie **nicht**: dort sollen alle stehen, das Regal
 * eingeschlossen. Es ist ja genau der Ort, an dem man es wiederfindet.
 */
export function nurVorraete<T extends { ziehbar: boolean }>(pools: readonly T[]): T[] {
  return pools.filter((p) => p.ziehbar)
}

/**
 * **Darf aus dieser Aufgabe überhaupt gezogen werden?**
 *
 * Übergeben werden **alle** Pools, in denen die Aufgabe liegt. Es reicht einer, der ziehbar
 * ist – dieselbe „die großzügigere Regel gewinnt"-Logik wie bei `wipDurchbrechen` unten. Wer
 * eine geparkte Aufgabe zusätzlich in „Tickets" legt, hat sie damit wieder zur Arbeit
 * erklärt; die Sperre soll das nicht überstimmen.
 *
 * Eine leere Liste heißt „liegt in keinem Pool" und wird eine Ebene höher beantwortet – dort
 * steht der bessere Satz dafür („vermutlich hat sie gerade jemand gezogen").
 */
export function poolsErlaubenZiehen(pools: readonly { ziehbar: boolean }[]): boolean {
  return pools.some((p) => p.ziehbar)
}

/**
 * **Kann ich mir diese Vorrat-Aufgabe jetzt nehmen?**
 * ([E-306](../../../docs/ENTSCHEIDUNGEN.md#e-306), 02.09.2026).
 *
 * Nils, nach dem ersten Entwicklertag: *„Im Vorlauf der Entwickler stehen anscheinend noch
 * Aufgaben von anderen."* Dazu aus Ticket NCW-6X848M: *„Aufgaben im Status Review/Abnahme sind
 * in Vorlauf"* und *„Vorlauf zeigt doppelte Aufgaben an"*.
 *
 * Drei Meldungen, eine Ursache: die Vorrat-Hälfte des Vorlaufs fragte nur *„nicht erledigt"* und
 * damit die falsche Frage. Der Vorlauf beantwortet **„was kann ich mir als Nächstes nehmen"** –
 * und eine Aufgabe, die jemand anders gerade bearbeitet, kann das nicht sein.
 *
 * Gemessen am 02.09.2026 über die 55 offenen Aufgaben in ziehbaren Vorräten:
 *
 * | | Anzahl |
 * |---|---|
 * | herrenlos – für jeden zu haben | 17 |
 * | in Work von jemandem **genommen** | 9 |
 * | nur in der **Quelle** jemandem zugeschrieben | 29 |
 * | Status `in Arbeit` oder `Review/Abnahme` | 7 |
 *
 * Ohne diese Regel standen alle 55 im Vorlauf **jedes** Entwicklers.
 *
 * ## Drei Bedingungen, und die dritte ist die entschiedene
 *
 * 1. **Der Status muss `Eingang` oder `Bereit` sein.** Was läuft oder auf Abnahme wartet, ist
 *    kein Vorschlag – daran kann niemand anfangen. `zurueckgestellt` fällt ohnehin heraus:
 *    Geparktes liegt in einem nicht ziehbaren Pool.
 * 2. **Ohne Bearbeiter: für jeden** – außer jemand steht unter „kommen in Frage", dann nur für
 *    diese ([E-415](../../../docs/ENTSCHEIDUNGEN.md#e-415), 19.10.2026). Das ist der Normalfall
 *    des Vorrats.
 * 3. **Mit Bearbeiter: nur für ihn** – und für die, die unter „kommen in Frage" stehen.
 *
 * ## Warum die dritte Bedingung eine Entscheidung war und keine Ableitung
 *
 * Bis hierher galt sie **nur im Sofort-Vorrat** (`sofortSichtbarFuer`, 24.08.2026), mit einer
 * ausdrücklichen Begründung dagegen, sie überall anzuwenden:
 *
 * > *„Hat einen Bearbeiter" sagt außerhalb von Sofort meistens nichts über Work aus: der Wert
 * > kommt oft aus der Quelle ([F-64](../../../docs/OFFENE-FRAGEN.md) – 29 von 43 Tickets, 15 von
 * > 26 Projektaufgaben). Dieselbe Regel dort angewandt hielte zwei Drittel des Vorrats zurück,
 * > ohne dass jemand in Work die Aufgaben tatsächlich hätte.*
 *
 * Das stimmt weiterhin – **und ist im Vorlauf genau das Gewollte.** Nils hat es am 02.09.2026
 * so entschieden: eine in GitLab schon jemandem zugeschriebene Aufgabe verschwindet aus dem
 * Vorlauf **der anderen**. Sie bleibt bei **ihrem** Menschen ganz oben ([E-155](../../../docs/ENTSCHEIDUNGEN.md#e-155)
 * – *„der soll die Aufgabe am besten sehen, um sie sich zu ziehen"*) und sie bleibt vollständig
 * in der **Pool-Ansicht**, die das Regal ist und den ganzen Bestand zeigen soll.
 *
 * Der Unterschied ist die Frage, die die jeweilige Ansicht stellt: der Vorrat zeigt *was es
 * gibt*, der Vorlauf *was ich tun kann*. Zwei Drittel weniger Vorschläge sind hier kein Verlust,
 * sondern der Zweck.
 *
 * ## Was diese Regel **nicht** tut
 *
 * Sie sperrt nichts. Wer eine Aufgabe trotzdem haben will, findet sie im Vorrat und kann sie
 * dort nehmen – `tasks.nehmen` prüft unverändert. Hier wird nur entschieden, was **vorgeschlagen**
 * wird.
 *
 * `durchbrichtWip` spielt keine Rolle mehr: die Regel hing nie am Sofort-Pool, sondern an der
 * Frage, ob die Aufgabe zu haben ist. Ein Sofort-Vorrat erbt sie jetzt wie jeder andere.
 */
export function zumNehmenBereit(
  aufgabe: {
    status: TaskStatus
    /** Der **wirksame** Bearbeiter – Work, sonst die Quelle. */
    bearbeiterId: string | null | undefined
    wunschIds?: readonly string[]
  },
  ichId: string | null | undefined,
): boolean {
  if (aufgabe.status !== 'eingang' && aufgabe.status !== 'bereit') return false
  /**
   * **Herrenlos, aber anderen vorgemerkt: nur für die Vorgemerkten** (19.10.2026,
   * [E-415](../../../docs/ENTSCHEIDUNGEN.md#e-415)). Nils: *„wenn man nicht betroffen ist,
   * sollten die da generell nicht auftauchen … sonst würde man ja nie gefühlt mal fertig sein."*
   * Was niemand hat, sucht die Leitung im Leitstand – nicht jeder in seinem Vorlauf.
   */
  if (!aufgabe.bearbeiterId) return !fuerAndereVorgemerkt(aufgabe, ichId)
  if (!ichId) return false
  return aufgabe.bearbeiterId === ichId || (aufgabe.wunschIds?.includes(ichId) ?? false)
}

/**
 * **Schlägt der Vorlauf diese Zeile aus diesem Pool vor?** (16.09.2026,
 * [E-353](../../../docs/ENTSCHEIDUNGEN.md#e-353))
 *
 * Nils: *„Ideen den Pool will ich da nicht anzeigen bzw. reinlaufen lassen, wenn die Aufgaben
 * kein Zeitfenster, keine Fälligkeit haben. (Wenn wohl, dann natürlich wohl.)"*
 *
 * Die zweite Frage neben `zumNehmenBereit`: jene fragt, ob die Aufgabe **zu haben** ist, diese,
 * ob der Pool sie überhaupt **anbietet**. Ein Pool mit `vorlaufNurMitTermin` bietet nur an, was
 * einen wirksamen Termin trägt – `hatTermin` ist dieselbe Ableitung, die auch „heute fällig"
 * an die Zeile schreibt (Fälligkeit, Zeitfenster, Meilenstein, Zieltermin), nicht ein zweiter
 * Termin-Begriff.
 *
 * ## Vorgemerkt gewinnt
 *
 * Wer ausdrücklich „in Frage kommt", soll einspringen können – das schlägt die Sperre in
 * `zumNehmenBereit`, und es schlägt auch diese: eine Idee, die jemand **mir** vorgemerkt hat,
 * ist kein Rauschen, sondern eine Bitte. Dieselbe Ausnahme wie in [E-316](../../../docs/ENTSCHEIDUNGEN.md#e-316).
 *
 * ## Was diese Regel **nicht** tut
 *
 * Sie sperrt nichts und macht den Vorrat nicht kleiner: die Pool-Ansicht zeigt weiter alles, und
 * wer eine Idee ohne Termin will, nimmt sie dort. Hier wird nur entschieden, was „Mein Tag"
 * **vorschlägt** – wie bei [E-306](../../../docs/ENTSCHEIDUNGEN.md#e-306).
 */
export function poolSchlaegtVor(
  pool: { vorlaufNurMitTermin: boolean },
  aufgabe: { hatTermin: boolean; wunschIds?: readonly string[] },
  ichId: string | null | undefined,
): boolean {
  if (!pool.vorlaufNurMitTermin) return true
  if (aufgabe.hatTermin) return true
  return !!ichId && (aufgabe.wunschIds?.includes(ichId) ?? false)
}

/**
 * **Ist diese Aufgabe für jemand anderen vorgemerkt?**
 *
 * Nils, 24.08.2026: *„Sollte man die nicht lieber auf Klick nur zeigen? In 99 % der Fälle fällt
 * jemand ja nur spontan kurz aus, wo der Leiter die dann verteilt. Das wäre sonst immer ein Wust
 * von Aufgaben für den Entwickler, die er in 1 % der Zeit nur sehen muss."*
 *
 * ## Warum das die Sortierung nicht ersetzt, sondern ablöst
 *
 * Zuerst wurden fremde Wunsch-Aufgaben nur **nach hinten** sortiert. Das behebt den falschen
 * ersten Vorschlag, aber nicht den Grund dahinter: sie bleiben im Strom, sie zählen in „Vorschlag
 * 1 von 72" mit, und man blättert an ihnen vorbei. Gemessen am 24.08.2026 sind es **16 von 81**
 * ziehbaren Aufgaben – wenig genug, dass es nie auffällt, und genug, dass es dauernd stört.
 *
 * Die Häufigkeiten geben Nils recht: ein Ausfall, bei dem jemand einspringen muss, ist die
 * Ausnahme, und dann verteilt ohnehin die Leitung. Dafür jeden Tag jeder Person sechzehn fremde
 * Zeilen zu zeigen, ist der falsche Tausch.
 *
 * **Weggenommen wird trotzdem nichts** ([E-87](../../../docs/ENTSCHEIDUNGEN.md), „Vorrang, kein
 * Anspruch"): sie stehen hinter einem Klick, mit Zahl daneben, und sind von dort mit Begründung
 * ziehbar. Der Unterschied ist nur, wer die Frage stellt – vorher der Vorrat, jetzt der Mensch.
 *
 * Ohne Sitzung gilt jede Vormerkung als fremd: wer nicht angemeldet ist, ist niemand Bestimmtes.
 */
export function fuerAndereVorgemerkt(
  aufgabe: { wunschIds?: readonly string[] },
  ichId: string | null | undefined,
): boolean {
  const w = aufgabe.wunschIds
  if (!w || w.length === 0) return false
  if (!ichId) return true
  return !w.includes(ichId)
}

/**
 * **Im Sofort-Pool ist „kommen in Frage" ein Anspruch, kein Vorrang** (14.09.2026,
 * [E-346](../../../docs/ENTSCHEIDUNGEN.md#e-346)).
 *
 * Nils zu [F-08](../../../docs/OFFENE-FRAGEN.md) (*„darf jeder aus dem Sofort-Pool ziehen?"*):
 * *„wenn kommt in Frage gefüllt, dann nur die."* Steht an einer Sofort-Aufgabe jemand unter
 * „kommen in Frage", nehmen und starten sie **nur diese Personen** – ohne den Umweg „trotzdem,
 * mit Grund" aus E-87. Steht dort niemand, darf jeder, wie bisher.
 *
 * **Warum nur im Sofort-Pool:** Eine Störung, die ausdrücklich an jemanden geht, ist eine
 * Zuteilung durch die Leitung; wer sie mit Begründung übernimmt, arbeitet an der Leitung
 * vorbei – und die Aufgabe „geht vor allem anderen", also passiert das auch schnell. In den
 * anderen Pools bleibt E-87: Vorrang, kein Anspruch.
 *
 * `poolDurchbrichtWip` ist das Kennzeichen des Sofort-Pools, wie bei `darfStarten`.
 * Ohne Sitzung gilt die Sperre – wer niemand ist, ist nicht vorgesehen.
 */
export function sofortNurFuerVorgesehene(
  aufgabe: { wunschIds?: readonly string[]; poolDurchbrichtWip: boolean },
  ichId: string | null | undefined,
): boolean {
  if (!aufgabe.poolDurchbrichtWip) return false
  return fuerAndereVorgemerkt(aufgabe, ichId)
}

/**
 * Darf diese Person noch etwas **anfangen**?
 *
 * Getrennt von der Datenbank gehalten, damit Oberfläche und Server dieselbe
 * Regel benutzen und nicht zwei Auslegungen entstehen. Der Server ist trotzdem
 * die verbindliche Prüfung – die Oberfläche darf nur nicht *anderes* behaupten.
 *
 * `wipDurchbrechen` ist der Grund, warum hier ein Pool hineinreicht: „Sofort
 * geht immer vor" (Kap. 4.3) heißt, dass eine Sofort-Aufgabe auch bei
 * ausgeschöpftem Limit begonnen werden darf. Ohne diese Ausnahme wäre der
 * Sofort-Pool bei einem vollen Team blockiert – also genau dann, wenn er
 * gebraucht wird.
 *
 * ## Hieß bis zum 24.08.2026 `darfZiehen` – und saß damit an der falschen Handlung
 *
 * Solange Ziehen *nehmen und anfangen in einem* war, gab es nur eine Stelle, an die das Limit
 * konnte. Seit [Z-06](../../../docs/phasen/zusatz-06-nehmen-und-starten.md) sind es zwei
 * Handgriffe, und das Limit gehört an den zweiten: **Füllen ist nie gesperrt, Anfangen
 * schon.** Ein voller Fokus ist kein Grund, sich nicht versorgen zu dürfen – er ist ein Grund,
 * nicht noch etwas anzufangen.
 */
export function darfStarten(lage: {
  inArbeit: number
  wipLimit: number
  poolDurchbrichtWip: boolean
}): { erlaubt: boolean; grund?: string } {
  if (lage.poolDurchbrichtWip) return { erlaubt: true }
  if (lage.inArbeit >= lage.wipLimit) {
    return {
      erlaubt: false,
      grund: `Dein Fokus ist voll (${lage.inArbeit} von ${lage.wipLimit}). Erst etwas abschließen – oder eine Sofort-Aufgabe starten. Nehmen darfst du weiter.`,
    }
  }
  return { erlaubt: true }
}

/**
 * **Wo „noch nicht eingeordnet" in der Reihenfolge steht** (18.08.2026,
 * [E-124](../../../docs/ENTSCHEIDUNGEN.md); **geändert am 01.10.2026**,
 * [E-381](../../../docs/ENTSCHEIDUNGEN.md#e-381)).
 *
 * Ein Termin ist eine Aussage in **beide** Richtungen: „heute" zieht nach vorn, „Quartal"
 * schiebt nach hinten. Kein Termin ist keine der beiden Aussagen – und steht deshalb nicht am
 * Ende.
 *
 * ## Warum „ohne" jetzt **hinter** „Monat" steht und nicht mehr davor
 *
 * E-124 stellte „ohne" vor **beide** fernen Stufen, und die Begründung hing an einer Zahl:
 * Damals hatten **0 von 141** offenen Aufgaben ein Zeitfenster. „Ohne zuletzt" hätte geheißen,
 * dass der Erste, der ein „Quartal" setzt, den halben Vorrat vor sich räumt.
 *
 * Am 30.09.2026 nachgemessen: **68 von 118 – 58 %.** Die Grundlage hat sich verschoben, und der
 * Preis wurde sichtbar. Davin, in der Review: *„Hier scheint die Sortierung in Vorlauf nicht zu
 * passen"* – angestrichen war eine Aufgabe **ohne Frist** über einer, die in drei Wochen fällig
 * war.
 *
 * Nils hat (01.10.2026) **Fassung C** gewählt, den Mittelweg: Ein Monatstermin schlägt „ohne",
 * ein Quartalstermin nicht. Damit bleibt E-124s eigentlicher Gedanke am Leben – *„Quartal
 * schiebt nach hinten"* –, während eine gesetzte Frist innerhalb des nächsten Monats auch
 * wirklich vorgeht.
 *
 * **Überfällig steht vor „heute"** – ein gerissener Termin wiegt schwerer als einer, der heute
 * abläuft. Dieselbe Reihenfolge wie in den Spalten der Zeitachse (`dringlichkeitVon`).
 */
const DRINGLICHKEIT_RANG: Record<Dringlichkeit, number> = {
  ueberfaellig: 0,
  sofort: 1,
  tage5: 2,
  tage10: 3,
  monat: 4,
  ohne: 5,
  quartal: 6,
}

/**
 * Sortierung innerhalb eines Pools: Dringlichkeit, dann Priorität, dann das Alter.
 *
 * **Die Dringlichkeit steht vorn**, weil sie die Frage *„wann muss das gemacht sein"*
 * beantwortet und die Priorität nur *„wie wichtig ist es"*. Eine B-Aufgabe für *heute* geht
 * vor eine A-Aufgabe fürs Quartal – sonst wäre der Termin ein Feld, das man setzen kann,
 * ohne dass etwas passiert.
 *
 * ## Die Dringlichkeit kommt aus dem wirksamen Termin, nicht aus der gesetzten Stufe (Z-15)
 *
 * Bis zum 14.09.2026 las der Vergleicher die Spalte `zeitfenster` – die Stufe, die jemand
 * gesetzt hatte. Damit fehlten zwei Teile der Regel, die seit [E-156](../../../docs/ENTSCHEIDUNGEN.md)
 * und [E-281](../../../docs/ENTSCHEIDUNGEN.md) überall sonst galt:
 *
 * | Fall | Anzeige an der Zeile | Reihenfolge bis dahin |
 * |---|---|---|
 * | Fälligkeit morgen, kein Zeitfenster | „morgen fällig" | wie „ohne" – hinter „10 Werktage" |
 * | „10 Werktage", vor acht Tagen gesetzt | „in 2 Tagen" | weiter wie „10 T" |
 *
 * Nils, 14.09.2026: *„Zeitfenster, Prio, Fälligkeit (Fälligkeit direkt gesetzt übersteigt
 * Zeitfenster) – das macht die Reihenfolge aus. Der errechnete Fälligkeitstermin wird ebenfalls
 * aus Zeitfenster (Zeitpunkt des Setzens + Zeitfenster) oder Fälligkeit direkt gewonnen."*
 *
 * Genau das leistet `wirksamerTermin` (Fälligkeit → Meilenstein → Zieltermin → Zeitfenster ab
 * Anker), und `dringlichkeitVon` macht daraus die Stufe. Die Zeile bringt den Termin fertig
 * gerechnet mit (`termin`) – **einmal je Aufgabe**, dort, wo Vorhaben und Meilensteine
 * bekannt sind. Hier wird nur noch eingestuft und verglichen. Zwei Rechnungen für „bis wann"
 * laufen sonst irgendwann auseinander, und niemand merkt es, weil beide plausibel aussehen.
 *
 * Der Sofort-Pool geht die Einstufung hier nichts an (`sofortPool: false`): sortiert wird
 * *innerhalb* eines Pools, und die Pool-Reihenfolge entsteht eine Ebene höher (E-209).
 */
export type PoolSortierbar = {
  /**
   * Der **wirksame Termin** (E-281) – `null` heißt wirklich „keiner", Stufe „ohne". Kommt aus
   * `wirksamerTermin(…)`, nicht aus einer einzelnen Spalte.
   */
  termin: Termin
  priority: string | null
  /**
   * `Date | string`, weil dieselbe Sortierung an zwei Orten läuft: im Server auf Zeilen mit
   * echten `Date`, im Client auf dem, was über tRPC als JSON ankommt – Zeichenketten. Genau
   * diese Doppelverwendung ist der Sinn der Funktion; `entstanden` nimmt beides.
   *
   * **Bewusst ohne `wunschIds`**, und das ist keine Vergesslichkeit: die Pool-Abfrage im Server
   * sortiert ihre **Rohzeilen**, und dort ist `wunschIds` noch die Zeichenkette aus
   * `GROUP_CONCAT` – zu `string[]` wird sie erst beim Zusammenbauen der Antwort. Stünde das Feld
   * hier mit dem Typ des Clients, wäre die Sortierung im Server nicht mehr aufrufbar.
   */
  sourceCreatedAt: Date | string | null
  createdAt: Date | string
}

/**
 * Die personenlose Reihenfolge – die, mit der der Server jeden Pool vorsortiert.
 *
 * `heute` ist ein Parameter und kein `new Date()` im Modul: der Server läuft Wochen, und eine
 * beim Laden eingefrorene Gegenwart hieße, dass dort nichts je überfällig würde.
 */
export function poolSortierung(heute: Date): (a: PoolSortierbar, b: PoolSortierbar) => number {
  return poolVergleicher(null, heute)
}

/**
 * **Dieselbe Reihenfolge, aber mit Blick auf eine Person** – der ★-Schritt aus
 * [E-209](../../../docs/ENTSCHEIDUNGEN.md).
 *
 * `poolVergleicher(meineId)` stellt Aufgaben, für die ich in Frage komme, vor gleich dringende
 * andere. **Als vorletztes Kriterium, nicht als erstes** – und das ist der ganze Punkt des
 * Eintrags:
 *
 * > Nils, 21.08.2026: *„sofort ist immer das wichtigste (bug, fehler usw.)"*
 *
 * Bis dahin hob `MeinTag.tsx` alle ★-Aufgaben über die **Pool-Grenze** hinweg nach oben. Damit
 * stand eine Sofort-Störung hinter zwei Aufgaben, die für den Betrachter vorgemerkt waren. Der
 * Leitsatz, der das auflöst: **Dringlichkeit vor Zuständigkeit.** Pool, Zeitfenster und
 * Priorität sagen, *wie dringend* etwas ist; ★ sagt nur, *für wen* es gedacht ist – und ein
 * Wer-Kriterium darf keines der drei überstimmen.
 *
 * Damit ist ★ das, als was es gemeint war ([E-87](../../../docs/ENTSCHEIDUNGEN.md)):
 * **Vorrang, kein Anspruch** – es entscheidet unter gleich dringenden Aufgaben, nicht gegen
 * sie. Sichtbar bleibt es unabhängig davon: an der Karte, im Ziehen-Fenster und als Filter.
 *
 * **Der Pool selbst steht nicht in dieser Funktion.** Sie sortiert *innerhalb* eines Pools;
 * die Reihenfolge der Pools ist ihr `sortOrder`, und wer über mehrere hinweg flach macht, muss
 * sie pro Pool anwenden und die Ergebnisse aneinanderhängen. Genau deshalb konnte der Fehler
 * überhaupt entstehen: ein `sort` über die schon flache Liste kennt die Pool-Grenze nicht mehr.
 */
export type MitWunsch = PoolSortierbar & {
  /** Wer für diese Aufgabe in Frage kommt. Fehlt die Angabe, wirkt der ★-Schritt einfach nicht. */
  wunschIds?: readonly string[]
}

export function poolVergleicher(
  ichId: string | null | undefined,
  heute: Date = new Date(),
): (a: MitWunsch, b: MitWunsch) => number {
  /**
   * Die Stufe je Zeile **einmal** rechnen, nicht bei jedem der n·log n Vergleiche: `sort`
   * fragt dieselbe Zeile dutzendfach, und `dringlichkeitVon` zählt Werktage.
   */
  const stufen = new WeakMap<MitWunsch, Dringlichkeit>()
  const stufe = (t: MitWunsch): Dringlichkeit => {
    let s = stufen.get(t)
    if (s === undefined) {
      s = dringlichkeitVon(t.termin, false, heute).stufe
      stufen.set(t, s)
    }
    return s
  }

  return (a, b) => {
    /**
     * **Was für mich vorgesehen ist, kommt zuerst – innerhalb des Pools** (Nils, 24.08.2026:
     * *„Der muss doch da, wo ich als Wunsch-Entwickler bin, vorziehen gegenüber Aufgaben ohne
     * Zuweisung oder von anderen. Das Wunsch-Entwickler ist ja genau dafür gekommen."*).
     *
     * ## Warum ★ jetzt über der Dringlichkeit steht
     *
     * Zuerst stand es darunter, mit dem Satz *„Dringlichkeit vor Zuständigkeit"*. Der Satz ist
     * für den **Pool** richtig ([E-209](../../../docs/ENTSCHEIDUNGEN.md), Sofort geht immer vor)
     * – für die Ebene darunter war er eine Fehlübertragung.
     *
     * Denn der Stern ist keine Aussage über die Person, sondern **die Planungsentscheidung
     * selbst**: hier hat jemand – der Leiter oder man selbst – festgelegt, dass diese Arbeit zu
     * dieser Person gehört. Eine herrenlose Aufgabe ist eine, die **niemand geplant hat**. Wer
     * Ungeplantes vor Geplantes stellt, dreht die Planung um.
     *
     * Der Preis, bewusst getragen: eine für mich vorgemerkte Aufgabe mit „30 Tage" steht vor
     * einer herrenlosen mit „5 Tage" – aber nur **innerhalb desselben Pools**. Was wirklich
     * brennt, gehört in den Sofort-Vorrat, und der schlägt weiterhin alles.
     *
     * ★ für **andere** taucht hier gar nicht mehr auf: das filtert `fuerAndereVorgemerkt` eine
     * Ebene höher heraus. Der Rang bleibt trotzdem dreistufig, damit die Reihenfolge auch dann
     * stimmt, wenn jemand sie ausdrücklich dazuholt.
     */
    /**
     * **Ein herrenloses „heute" schlägt den Stern trotzdem** (Nils, 24.08.2026: *„Ein
     * herrenloses JETZT oder Pool Sofort sollte weiter Prio haben. Nur die anderen nicht."*).
     *
     * Die Ausnahme über der Ausnahme, und sie ist eng: **nur, was heute fällig oder schon
     * überfällig ist.** „5 Tage" oder „30 Tage" sind Fristen – da ist die Planung wichtiger als
     * der Kalender. „Heute" ist keine Frist, sondern die Ansage *jetzt*; wer die hinter eine
     * Vormerkung schiebt, macht aus der höchsten Dringlichkeitsstufe eine unter vielen. Und
     * Überfälliges ist die Ansage, die schon gestern galt.
     *
     * Seit Z-15 heißt „jetzt" nicht mehr die gesetzte Stufe, sondern der gerechnete Termin:
     * ein Zeitfenster „Jetzt" landet dort, eine Fälligkeit heute ebenso, und ein „5 Werktage"
     * von vor einer Woche auch.
     *
     * **Nur für Herrenloses und Eigenes**, nicht für fremd Vorgemerktes: Nils' Satz sagt
     * *„ein herrenloses JETZT"*. Was ausdrücklich für jemand anderen gedacht ist, bleibt hinten
     * – im Alltag ist es ohnehin ausgeblendet, und wer es dazuholt, soll es nicht als Erstes
     * vorgeschlagen bekommen.
     *
     * Der Sofort-Vorrat braucht diese Regel nicht – er steht ohnehin über allem, weil je Pool
     * sortiert wird (E-209).
     */
    const istJetzt = (t: MitWunsch) => {
      const s = stufe(t)
      return (s === 'sofort' || s === 'ueberfaellig') && !fuerAndereVorgemerkt(t, ichId) ? 0 : 1
    }
    const nachJetzt = istJetzt(a) - istJetzt(b)
    if (nachJetzt !== 0) return nachJetzt

    if (ichId) {
      const wunschRang = (t: MitWunsch) => {
        const w = t.wunschIds
        if (!w || w.length === 0) return 1
        return w.includes(ichId) ? 0 : 2
      }
      const nachWunsch = wunschRang(a) - wunschRang(b)
      if (nachWunsch !== 0) return nachWunsch
    }

    const nachDringlichkeit = DRINGLICHKEIT_RANG[stufe(a)] - DRINGLICHKEIT_RANG[stufe(b)]
    if (nachDringlichkeit !== 0) return nachDringlichkeit

    const rang = (p: string | null) => (p === 'A' ? 0 : p === 'B' ? 1 : p === 'C' ? 2 : 3)
    const unterschied = rang(a.priority) - rang(b.priority)
    if (unterschied !== 0) return unterschied

    /**
     * **Innerhalb einer Stufe entscheidet der Termin selbst** (30.09.2026, Review-Fund von
     * Davin: *„Hier scheint die Sortierung in Vorlauf nicht zu passen"*).
     *
     * Die Stufe ist ein **Eimer**, kein Datum: „bis 08.10." und „bis 09.10." fallen im
     * September beide in `tage10`. Bis hierher entschied dann das **Entstehungsdatum** – und
     * damit stand im Vorlauf die Aufgabe mit der späteren Frist über der mit der früheren.
     * In Davins Bild waren es genau diese zwei Tage.
     *
     * Ein Datum, das man kennt, schlägt eine Reihenfolge, die nur aus der Ablage stammt.
     *
     * **Nur innerhalb derselben Stufe** – wo die Stufen sich unterscheiden, hat oben schon
     * `DRINGLICHKEIT_RANG` entschieden, und dort steht „ohne Termin" bewusst in der Mitte
     * ([E-124](../../../docs/ENTSCHEIDUNGEN.md)). Beide Zeilen hier haben entweder einen
     * Termin oder beide keinen; der zweite Fall fällt durch und landet beim Alter.
     */
    const terminA = a.termin?.datum.getTime() ?? null
    const terminB = b.termin?.datum.getTime() ?? null
    if (terminA !== null && terminB !== null && terminA !== terminB) return terminA - terminB

    // `entstanden` statt `sourceCreatedAt`: in Work angelegte Aufgaben haben dort
    // nichts stehen und zählen ab ihrem Anlagedatum.
    return entstanden(a).getTime() - entstanden(b).getTime()
  }
}

/* ── Start-Pools für einen neuen Workspace (Z-07.16) ─────────────────── */

/**
 * **Womit ein neuer Workspace anfängt** (Nils, 25.08.2026, auf die Frage nach Start-Pools:
 * *„Ja, kleiner Satz"*).
 *
 * ## Warum überhaupt automatisch – wo Pools doch selbst angelegt werden sollen
 *
 * Weil ein Workspace **ohne** Pool nicht bloß leer ist, sondern kaputt: kein Eingang, kein
 * Betanken, kein Ziehen, und „Parken" findet kein Ziel. Genau das ist am 25.08.2026 passiert –
 * „Marketing" war angelegt und nicht benutzbar, und von außen sah das aus wie ein Fehler.
 *
 * Der Satz ist deshalb bewusst **klein**: drei Pools, keine Nachbildung der Entwicklung. Er
 * soll den Workspace zum Laufen bringen und nicht behaupten, er wüsste, wie dort gearbeitet
 * wird. Umbenennen, löschen, erweitern geht ab der ersten Sekunde.
 *
 * ## Warum der persönliche weniger bekommt
 *
 * „Sofort" ist ein Vorrat mit anderen Spielregeln für ein **Team** – er darf das WIP-Limit
 * durchbrechen, weil eine Störung nicht warten kann, bis jemand frei wird. In den eigenen
 * Todos gibt es weder Team noch WIP-Limit ([E-238](../../../docs/ENTSCHEIDUNGEN.md#e-238));
 * ein „Sofort" wäre dort eine Prioritätsstufe, und genau das ist ein Pool nicht.
 */
export interface StartPool {
  schluessel: PoolKey
  label: string
  icon: string
  sortOrder: number
  limitAnzahl: number | null
  regeln: string
  wipDurchbrechen: boolean
  jederDarfZiehen: boolean
  ziehbar: boolean
  vorlaufNurMitTermin: boolean
}

const SOFORT: StartPool = {
  schluessel: 'sofort',
  label: 'Sofort',
  icon: '🔥',
  sortOrder: 1,
  limitAnzahl: 5,
  regeln: 'Geht immer vor und darf das persönliche WIP-Limit durchbrechen.',
  wipDurchbrechen: true,
  jederDarfZiehen: true,
  ziehbar: true,
  vorlaufNurMitTermin: false,
}

/**
 * **„Aufgaben" und nicht „Tickets & Kleinigkeiten".** Der Schlüssel `tickets` bleibt – er
 * trägt Farbe und Bedeutung –, die Beschriftung ist allgemein: ein Marketing-Workspace hat
 * keine Tickets, und ein Pool, der nach etwas benannt ist, das es dort nicht gibt, wird als
 * Erstes umbenannt.
 */
const AUFGABEN: StartPool = {
  schluessel: 'tickets',
  label: 'Aufgaben',
  icon: '🎫',
  sortOrder: 2,
  limitAnzahl: null,
  regeln: 'Was hier liegt, kann jeder ziehen. Sortiert nach Zeitfenster, dann Priorität.',
  wipDurchbrechen: false,
  jederDarfZiehen: true,
  ziehbar: true,
  vorlaufNurMitTermin: false,
}

const PARKPLATZ: StartPool = {
  schluessel: 'parkplatz',
  label: 'Parkplatz',
  icon: '🅿️',
  sortOrder: 90,
  limitAnzahl: null,
  regeln:
    'Nur zur Kenntnis – hier wird nichts gezogen. Was hier liegt, ist geparkt, bis daraus ' +
    'eine Aufgabe wird; der Weg zurück führt in den Eingang.',
  wipDurchbrechen: false,
  jederDarfZiehen: true,
  ziehbar: false,
  vorlaufNurMitTermin: false,
}

export function startPoolsFuer(art: WorkspaceArt): StartPool[] {
  return art === 'persoenlich' ? [AUFGABEN, PARKPLATZ] : [SOFORT, AUFGABEN, PARKPLATZ]
}

/**
 * **Die Kennung eines Pools in einem Workspace** – `marketing-parkplatz`.
 *
 * Die vier Pools der Entwicklung behalten ihre bloßen Kennungen (`sofort`, `parkplatz`); sie
 * waren zuerst da, und sie umzuschlüsseln hieße, 3.200 Zeilen in `task_pool` anzufassen
 * ([E-236](../../../docs/ENTSCHEIDUNGEN.md#e-236)). Alles Neue wird zusammengesetzt.
 *
 * **Auf 32 Zeichen gekürzt**, weil `pool.id` so breit ist. Gekürzt wird am **Workspace**, nicht
 * am Schlüssel: `technischer-service-parkplatz` ist zu lang, und `technischer-ser-parkplatz`
 * bleibt lesbar – `technischer-service-parkpl` nicht. Ob die gekürzte Kennung frei ist, prüft
 * der Server; er hängt sonst eine Zahl an.
 */
export function poolIdVorschlag(workspaceId: string, schluessel: string): string {
  const platz = 32 - schluessel.length - 1
  return `${workspaceId.slice(0, Math.max(1, platz))}-${schluessel}`
}

/**
 * **Schlägt „Mein Tag" diese Aufgabe überhaupt vor?** – über alle Pools, in denen sie liegt
 * ([E-394](../../../docs/ENTSCHEIDUNGEN.md#e-394), 01.10.2026).
 *
 * `poolSchlaegtVor` beantwortet die Frage **je Pool** und wurde deshalb nur dort gefragt, wo
 * eine Zeile aus einem Vorrat kommt. Nils, 01.10.2026, nachdem er vier termin- und priolose
 * Ideen im Rad fand: *„Da dürfen nur wirklich Sachen aus dem Vorlauf rein."*
 *
 * Beim Nachmessen stellte sich heraus, dass sie **auch im Vorlauf** standen – die Regel greift
 * nämlich nur für Fremdes. Was mir schon gehört, wurde nie gefragt, und bei jemandem, der fast
 * alles sich selbst zuweist, griff sie damit praktisch nie.
 *
 * ## Warum „irgendein Pool genügt"
 *
 * Eine Aufgabe kann in mehreren Vorräten liegen. Bietet **einer** sie an, ist sie im Angebot –
 * dass ein zweiter Vorrat strenger ist, macht sie nicht unsichtbar. Erst wenn **alle** ihre
 * Vorräte sie zurückhalten, verschwindet sie aus dem Vorschlag.
 *
 * ## Ohne Pool gilt keine Pool-Regel
 *
 * Eine Aufgabe, die in keinem Vorrat liegt, wird von keinem zurückgehalten. Das ist kein
 * Schlupfloch, sondern die Bedeutung von „der Pool bietet an": Wo keiner ist, bietet keiner an
 * – und dann entscheidet allein, ob sie mir gehört.
 */
export function vorlaufBietetAn(
  aufgabe: {
    hatTermin: boolean
    wunschIds?: readonly string[]
    poolIds?: readonly string[] | null
  },
  pools: readonly { id: string; vorlaufNurMitTermin: boolean }[],
  ichId: string | null | undefined,
): boolean {
  const ids = aufgabe.poolIds
  if (!ids || ids.length === 0) return true
  const ihre = pools.filter((p) => ids.includes(p.id))
  if (ihre.length === 0) return true
  return ihre.some((p) => poolSchlaegtVor(p, aufgabe, ichId))
}
